import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';

interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: LucideIcon;
  variant?: 'primary' | 'secondary';
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  tips?: string[];
  className?: string;
  variant?: 'default' | 'card' | 'dashed';
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  tips,
  className,
  variant = 'default',
}: EmptyStateProps) {
  const variantClasses = {
    default: 'bg-white rounded-xl border border-gray-200 shadow-sm',
    card: 'bg-gray-50 rounded-xl border border-gray-200',
    dashed: 'bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-300',
  };

  const renderAction = (actionConfig: EmptyStateAction, isPrimary: boolean) => {
    const baseClasses = isPrimary
      ? 'inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2'
      : 'inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors';

    const ActionIcon = actionConfig.icon;

    if (actionConfig.href) {
      return (
        <a href={actionConfig.href} className={baseClasses}>
          {ActionIcon && <ActionIcon className="h-4 w-4" />}
          {actionConfig.label}
        </a>
      );
    }

    return (
      <button onClick={actionConfig.onClick} className={baseClasses}>
        {ActionIcon && <ActionIcon className="h-4 w-4" />}
        {actionConfig.label}
      </button>
    );
  };

  return (
    <div className={clsx(variantClasses[variant], 'p-12 text-center', className)}>
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <Icon className="h-8 w-8 text-amber-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>
      {description && (
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">{description}</p>
      )}

      {tips && tips.length > 0 && (
        <div className="mt-4 mx-auto max-w-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
            Quick tips
          </p>
          <ul className="text-left text-sm text-gray-500 space-y-1">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(action || secondaryAction) && (
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {action && renderAction(action, action.variant !== 'secondary')}
          {secondaryAction && renderAction(secondaryAction, false)}
        </div>
      )}
    </div>
  );
}
